"use client";

// EVERY OPEN OF EVERY SIGNED-IN PAGE, TIMED IN THE BROWSER AND SENT AS ONE
// BEACON: App health → Opens (lib/ops-opens.ts says what is kept). Renders
// nothing.
//
// lib/open-beacon.ts says which navigations are opens, how the paints are
// seen and what that costs; lib/open-timing.mjs when an open felt loaded and
// settled. This file is the wiring: the person's clicks and keys and
// `popstate` feed the gate; a change of the address on screen (a layout
// effect, so the new page is in the DOM and not yet painted) may begin an
// open; a document load begins one at mount. An open collects until the page
// is still or 10 s have passed, then goes by navigator.sendBeacon, which
// outlives the page. Leaving the tab (or opening the next page) ends the open
// at once with what it saw.
//
// ⚠️ NEVER THE REASON A PAGE BREAKS. Every callback is inside a try; a browser
// without an API simply measures less (and the open says so: `sees`,
// `painted`). Nothing here reads layout or style.

import { useEffect, useLayoutEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { collectUntil, createOpenGate, inAppLink, isTyping, keepPaint, MAX_TAGS, OPEN_ENDPOINT, openResult, tagForTiming } from "@/lib/open-beacon";
import type { Paint, Shift } from "@/lib/open-timing.mjs";
import type { OpenBeacon, OpenKind } from "@/lib/ops-opens";

/** One open being timed. Times are on the page's clock (performance.now()). */
interface Live {
  kind: OpenKind;
  path: string;
  t0: number;
  began: number;
  paints: Paint[];
  largest: { area: number };
  shifts: Shift[];
  sees: boolean;
  frame: number | null;
  lastActivity: number;
  tags: number;
  /** The beacon's own main-thread time for this open, ms: its callbacks and its walks. */
  cost: number;
  timer: ReturnType<typeof setTimeout> | null;
  observers: Array<{ disconnect(): void; takeRecords?(): unknown[] }>;
}

/** The last opens this tab reported, newest last, with what each cost: for a spec, or someone in DevTools. */
type Reported = OpenBeacon & { cost: number };
declare global {
  interface Window {
    __opens?: Reported[];
  }
}

const KEEP_REPORTED = 20;
const LONE_KEYS = new Set(["Shift", "Control", "Alt", "Meta", "Tab", "Escape", "CapsLock"]);

const supports = (type: string): boolean => {
  try {
    return (PerformanceObserver.supportedEntryTypes ?? []).includes(type);
  } catch {
    return false;
  }
};

function send(b: OpenBeacon, cost: number): void {
  try {
    const body = JSON.stringify(b);
    const sent = typeof navigator.sendBeacon === "function" && navigator.sendBeacon(OPEN_ENDPOINT, new Blob([body], { type: "application/json" }));
    if (!sent) void fetch(OPEN_ENDPOINT, { method: "POST", body, keepalive: true, headers: { "content-type": "application/json" } }).catch(() => {});
  } catch {
    // an open that could not be sent is one fewer row, never an error
  }
  const list = (window.__opens ??= []);
  list.push({ ...b, cost: Math.round(cost * 100) / 100 });
  if (list.length > KEEP_REPORTED) list.splice(0, list.length - KEEP_REPORTED);
}

function finish(live: Live): void {
  if (live.timer !== null) clearTimeout(live.timer);
  for (const o of live.observers) {
    try {
      o.takeRecords?.();
      o.disconnect();
    } catch {
      // gone already
    }
  }
  const now = performance.now();
  send(
    openResult({ kind: live.kind, path: live.path, paints: live.paints, shifts: live.shifts, sees: live.sees, frame: live.frame ?? now - live.t0 }),
    live.cost,
  );
}

/** Begin timing an open; `t0` is what began it, on the page's clock. */
function begin(kind: OpenKind, t0: number, path: string): Live {
  const start = performance.now();
  const live: Live = {
    kind,
    path,
    t0,
    began: kind === "hard" ? 0 : start,
    paints: [],
    largest: { area: 0 },
    shifts: [],
    sees: supports("layout-shift"),
    frame: null,
    lastActivity: start,
    tags: 0,
    cost: 0,
    timer: null,
    observers: [],
  };
  const timed = (fn: () => void) => () => {
    const a = performance.now();
    try {
      fn();
    } catch {
      // never the page's problem
    }
    live.cost += performance.now() - a;
  };
  const watch = (type: string, onEntry: (e: PerformanceEntry) => void, buffered: boolean) => {
    if (!supports(type)) return;
    try {
      const po = new PerformanceObserver((list) => timed(() => list.getEntries().forEach(onEntry))());
      po.observe({ type, buffered });
      live.observers.push(po);
    } catch {
      // not this browser
    }
  };
  const seen = (at: number) => {
    if (at > live.lastActivity) live.lastActivity = at;
  };
  const primary = (el: Element | null | undefined) => !!el && typeof el.closest === "function" && el.closest("[data-primary]") !== null;

  timed(() => {
    watch(
      "layout-shift",
      (e) => {
        const s = e as PerformanceEntry & { value: number };
        if (s.startTime < t0) return;
        live.shifts.push({ t: s.startTime - t0, value: s.value });
        seen(s.startTime);
      },
      true,
    );
    const root = document.querySelector("main") ?? document.body;
    if (kind === "hard") {
      // The document's own LCP candidates, from the navigation's start: nothing to tag.
      watch(
        "largest-contentful-paint",
        (e) => {
          const p = e as PerformanceEntry & { size: number; element: Element | null };
          keepPaint(live.paints, { t: p.startTime, area: p.size, primary: primary(p.element) }, live.largest);
          seen(p.startTime);
        },
        true,
      );
      const fcp = performance.getEntriesByName("first-contentful-paint")[0];
      live.frame = fcp ? fcp.startTime : null;
    } else {
      watch(
        "element",
        (e) => {
          const p = e as PerformanceEntry & { renderTime: number; loadTime: number; intersectionRect: DOMRectReadOnly; element: Element | null };
          const t = (p.renderTime || p.loadTime) - t0;
          const r = p.intersectionRect;
          const area = r ? r.width * r.height : 0;
          // Off screen is no paint anyone saw: no lookup for it.
          keepPaint(live.paints, { t, area, primary: area > 0 && primary(p.element) }, live.largest);
          seen(p.renderTime || p.loadTime);
        },
        false,
      );
      // The new page is in the DOM and not yet painted: tag it now, before its frame.
      live.tags += tagForTiming(root, MAX_TAGS);
      requestAnimationFrame((ts) => {
        if (live.frame === null) live.frame = ts - t0;
      });
    }
    const mo = new MutationObserver(
      (records) =>
        timed(() => {
          seen(performance.now());
          if (kind !== "soft") return;
          for (const m of records) {
            for (const n of m.addedNodes) {
              if (live.tags >= MAX_TAGS) return;
              live.tags += tagForTiming(n, MAX_TAGS - live.tags);
            }
          }
        })(),
    );
    mo.observe(root, { childList: true, subtree: true, characterData: true });
    live.observers.push(mo);
  })();

  const check = () => {
    const now = performance.now();
    const until = collectUntil(live.t0, live.began, live.lastActivity);
    if (now >= until) {
      live.timer = null;
      current.live = null;
      finish(live);
    } else live.timer = setTimeout(check, Math.max(50, until - now));
  };
  live.timer = setTimeout(check, Math.max(50, collectUntil(live.t0, live.began, live.lastActivity) - performance.now()));
  return live;
}

/** This tab's open being timed: one at a time (the next ends the last). `hard`: the document load has been begun. */
const current: { live: Live | null; hard: boolean } = { live: null, hard: false };

function end(): void {
  const live = current.live;
  current.live = null;
  if (live) finish(live);
}

export function OpenBeacon() {
  const pathname = usePathname();
  const search = useSearchParams();
  const query = search.toString();
  const address = query === "" ? pathname : `${pathname}?${query}`;
  const gate = useRef<ReturnType<typeof createOpenGate> | null>(null);

  // A change of the address on screen, in the commit that drew the new page.
  useLayoutEffect(() => {
    try {
      if (gate.current === null) {
        gate.current = createOpenGate(address);
        return;
      }
      const t0 = gate.current.changed(address, performance.now());
      if (t0 === null) {
        // Part of the open in progress (a redirect, the `?as=` stamp): the open is of the page it lands on.
        if (current.live) current.live.path = pathname;
        return;
      }
      end();
      current.live = begin("soft", t0, pathname);
    } catch {
      // never the page's problem
    }
  }, [address, pathname]);

  useEffect(() => {
    const g = gate.current;
    if (g === null) return;
    const onClick = (e: MouseEvent) => {
      try {
        g.cause(inAppLink(e, location.origin) ? "link" : "input", e.timeStamp);
      } catch {
        // never
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e.target)) g.typed();
      else if (!LONE_KEYS.has(e.key)) g.cause("input", e.timeStamp);
    };
    const onPop = (e: PopStateEvent) => g.cause("traverse", e.timeStamp);
    const onHide = () => {
      if (document.visibilityState === "hidden") end();
    };
    document.addEventListener("click", onClick, { capture: true, passive: true });
    document.addEventListener("keydown", onKey, { capture: true, passive: true });
    window.addEventListener("popstate", onPop);
    window.addEventListener("pagehide", end);
    document.addEventListener("visibilitychange", onHide);
    // The document load: only one shown as it loaded (a tab opened in the
    // background paints nothing anyone waited for).
    try {
      if (!current.hard && document.visibilityState === "visible" && performance.getEntriesByType("navigation").length > 0) {
        current.hard = true;
        if (current.live === null) current.live = begin("hard", 0, location.pathname);
      }
    } catch {
      // never
    }
    return () => {
      document.removeEventListener("click", onClick, { capture: true });
      document.removeEventListener("keydown", onKey, { capture: true });
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("pagehide", end);
      document.removeEventListener("visibilitychange", onHide);
    };
  }, []);

  return null;
}
